import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { RouterProvider } from "react-router-dom";
import { queryClient } from "./lib/react-query.js";
import { router } from "./routes.js";
import "@fontsource-variable/jetbrains-mono";
import "./index.css";

const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_SHA ?? "dev",
    tracesSampleRate: 0.1,
    integrations: [],
  });
}

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found");

createRoot(root).render(
  <React.StrictMode>
    <HelmetProvider>
      <Sentry.ErrorBoundary
        fallback={<div role="alert">An unexpected error occurred. We have been notified.</div>}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>,
);

// Share-target handoff (Android "Share to unpii") needs sw.js registered.
// Guarded so this is a no-op in browsers without the API and in jsdom test
// environments (navigator.serviceWorker is undefined there) - never calls
// .register() unless the API actually exists. Registration failure is
// logged with context, not swallowed; it just isn't fatal to the app, since
// nothing else here depends on the service worker being active.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
      console.error("main: service worker registration failed", err);
    });
  });
}
